import { notifications } from '@mantine/notifications';
import { IconMoodPuzzled } from '@tabler/icons-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { isNetworkError } from '@xver/libs/connectionNotifier';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Endpoints } from '../../../../../apis';
import { InstanceActivityResponseModel } from '../../../../../apis/v1/instances/model';

export function useInstanceActivityInfiniteQuery(instanceId: number, pageSize = 20) {
    const { t } = useTranslation('cloudComputeInstance');
    const hasShownError = useRef(false);

    const query = useInfiniteQuery({
        queryKey: ['api.v1.instances.activity.infinite', instanceId, pageSize],
        queryFn: ({ pageParam }): Promise<InstanceActivityResponseModel> =>
            Endpoints.v1.Instances.getInstanceActivityLogs(instanceId, {
                limit: pageSize,
                cursor: pageParam,
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last: InstanceActivityResponseModel) => last.next_cursor ?? undefined,
        enabled: instanceId > 0,
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    });

    const { error } = query;

    useEffect(() => {
        if (error && !isNetworkError(error) && !hasShownError.current) {
            hasShownError.current = true;
            notifications.show({
                title: t('activity.queryFailedTitle'),
                message: t('activity.queryFailedMessage'),
                icon: <IconMoodPuzzled size={32} color={'#eb5757'} />,
            });
        }

        if (!error) {
            hasShownError.current = false;
        }
    }, [error, t]);

    return query;
}
